/**
 * useGlobalReservationSync.ts
 *
 * アプリケーション全体での予約データ同期管理用フック
 * App.jsなどで使用し、コンポーネントのマウント状況に関わらず同期を継続させる
 */

import { useEffect, useState, useCallback } from 'react';
import { reservationSyncService } from '../services/reservationSyncService';
import { vehicleSyncService } from '../services/vehicleSyncService';

const POLLING_COOKIE = 'calv2_polling_enabled';

function getPollingCookie(): boolean {
    try {
        const raw = document.cookie.split('; ').find(c => c.startsWith(`${POLLING_COOKIE}=`));
        return raw ? raw.split('=')[1] === 'true' : false;
    } catch { return false; }
}

function setPollingCookie(enabled: boolean): void {
    document.cookie = `${POLLING_COOKIE}=${enabled}; path=/; max-age=${60 * 60 * 24 * 365}`;
}

export function useGlobalReservationSync(enabled: boolean = true) {
    const [pollingEnabled, setPollingEnabled] = useState<boolean>(getPollingCookie);

    const togglePolling = useCallback(() => {
        setPollingEnabled(prev => {
            const next = !prev;
            setPollingCookie(next);
            return next;
        });
    }, []);

    // 初回データ取得（認証時に1回だけ）
    useEffect(() => {
        if (!enabled) return;
        reservationSyncService.startOnce();
        vehicleSyncService.startOnce();
        return () => {
            reservationSyncService.stop();
            vehicleSyncService.stop();
        };
    }, [enabled]);

    // ポーリング制御（トグルで即時切り替え）
    useEffect(() => {
        if (!enabled) return;
        if (pollingEnabled) {
            reservationSyncService.startPolling();
            vehicleSyncService.startPolling();
            console.log('[GlobalSync] Polling ON');
        } else {
            reservationSyncService.stopPolling();
            vehicleSyncService.stopPolling();
            console.log('[GlobalSync] Polling OFF');
        }
        return () => {
            reservationSyncService.stopPolling();
            vehicleSyncService.stopPolling();
        };
    }, [enabled, pollingEnabled]);

    return { pollingEnabled, togglePolling };
}
